import React, { isValidElement, ReactElement, ReactNode } from 'react';
import { nFormatter } from '@/utils/experience-utils';
import { TableCellLink } from '@/features/ai-agents/chat/MarkdownTableVisualization/styles';
import {
  MarkdownTableBody,
  MarkdownTableCell,
  MarkdownTableHead,
  MarkdownTableHeader,
  MarkdownTableRow,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/TableView';
import {
  PieValueLabelProps,
  RenderedTable,
  XAxisTickProps,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import {
  isLinkCellValue,
  parseNumericValue,
  resolveTableCellLinkHref,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

/**
 * Turns a raw table cell value into a clickable link when it looks like a
 * link (see `isLinkCellValue`). Non-string content (e.g. already rendered
 * markdown elements) is returned unchanged.
 */
export function renderLinkableCellValue(
  value: ReactNode,
  siteDomain?: string,
): ReactNode {
  if (typeof value !== `string` || !isLinkCellValue(value)) {
    return value;
  }

  return (
    <TableCellLink
      href={resolveTableCellLinkHref(value, siteDomain)}
      target="_blank"
      rel="noreferrer"
    >
      {value}
    </TableCellLink>
  );
}

const MAX_X_AXIS_LABEL_LENGTH = 24;
const MAX_X_AXIS_LABEL_LINES = 3;
const MIN_X_AXIS_LABEL_LENGTH = 4;
const X_AXIS_LABEL_CHARACTER_WIDTH = 7;
const X_AXIS_LABEL_HORIZONTAL_PADDING = 8;
const X_AXIS_LABEL_LINE_HEIGHT = 14;
const X_AXIS_LABEL_TOP_PADDING = 24;
const X_AXIS_LABEL_VIEWPORT_HEIGHT = 64;

export function formatChartMetricValue(
  value: number | string,
  metricLabel: string,
  symbol: string,
) {
  const numericValue =
    typeof value === `number` ? value : parseNumericValue(value);

  if (numericValue === null || !Number.isFinite(numericValue)) {
    return String(value);
  }

  const sign = numericValue < 0 ? `-` : ``;
  const compactValue = `${sign}${nFormatter(Math.abs(numericValue), 2)}`;

  if (isCurrencyMetric(metricLabel)) {
    return `${symbol}${compactValue}`;
  }

  return isPercentageMetric(metricLabel) ? `${compactValue}%` : compactValue;
}

export function isCurrencyMetric(metricLabel: string) {
  const normalizedLabel = normalizeMetricLabel(metricLabel);
  const labelParts = normalizedLabel.split(`_`);

  if (isPercentageMetric(metricLabel)) {
    return false;
  }

  return (
    labelParts.includes(`revenue`) ||
    labelParts.includes(`profit`) ||
    [
      `aov`,
      `average_order_value`,
      `avg_order_value`,
      `per_session_value`,
      `product_psv`,
      `psv`,
    ].includes(normalizedLabel)
  );
}

export function isPercentageMetric(metricLabel: string) {
  const normalizedLabel = normalizeMetricLabel(metricLabel);
  const labelParts = normalizedLabel.split(`_`);
  const percentageMarkers = [
    `growth`,
    `margin`,
    `percent`,
    `percentage`,
    `rate`,
    `uplift`,
  ];

  return (
    metricLabel.includes(`%`) ||
    percentageMarkers.some((marker) => labelParts.includes(marker)) ||
    [`ctr`, `returns_per_session`, `win_chance`].includes(normalizedLabel)
  );
}

function normalizeMetricLabel(metricLabel: string) {
  return metricLabel
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, `_`)
    .replace(/^_|_$/g, ``);
}

export function PieValueLabel({
  payload,
  textAnchor,
  value,
  x,
  y,
}: PieValueLabelProps) {
  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      fontSize={12}
      fontWeight={600}
      textAnchor={textAnchor}
      dominantBaseline="central"
    >
      {payload?.formattedY ?? String(value ?? ``)}
    </text>
  );
}

export function TruncatedXAxisTick({
  centered = false,
  getTooltip,
  index,
  payload,
  visibleTicksCount,
  width,
  x = 0,
  y = 0,
}: XAxisTickProps) {
  const fullLabel = String(payload?.value ?? ``);
  const labelWidth = getXAxisLabelWidth(width, visibleTicksCount);
  const { isTruncated, lines: labelLines } = splitXAxisLabel(
    fullLabel,
    getXAxisLabelLength(labelWidth),
  );
  const tooltipText =
    getTooltip?.(fullLabel) ?? (isTruncated ? fullLabel : null);
  const textAnchor = centered
    ? `middle`
    : getXAxisTextAnchor(index, visibleTicksCount);
  const viewportX = getXAxisLabelViewportX(textAnchor, labelWidth);
  const textX = getXAxisLabelTextX(textAnchor, labelWidth);

  return (
    <g transform={`translate(${x},${y})`}>
      <svg
        x={viewportX}
        y={0}
        width={labelWidth}
        height={X_AXIS_LABEL_VIEWPORT_HEIGHT}
        overflow="hidden"
      >
        <text
          x={textX}
          y={X_AXIS_LABEL_TOP_PADDING}
          fill="#666"
          fontSize={12}
          textAnchor={textAnchor}
        >
          {tooltipText && <title>{tooltipText}</title>}
          {labelLines.map((line, lineIndex) => (
            <tspan
              key={`${line}-${lineIndex}`}
              x={textX}
              dy={lineIndex === 0 ? 0 : X_AXIS_LABEL_LINE_HEIGHT}
            >
              {line}
            </tspan>
          ))}
        </text>
      </svg>
    </g>
  );
}

function splitXAxisLabel(label: string, lineLength: number) {
  const maximumVisibleLength = lineLength * MAX_X_AXIS_LABEL_LINES;
  const isTruncated = label.length > maximumVisibleLength;
  const visibleLabel = isTruncated
    ? `${label.slice(0, maximumVisibleLength - 3)}...`
    : label;

  return {
    isTruncated,
    lines: visibleLabel.match(new RegExp(`.{1,${lineLength}}`, `g`)) ?? [``],
  };
}

function getXAxisLabelLength(labelWidth: number) {
  const labelLength = Math.floor(
    (labelWidth - X_AXIS_LABEL_HORIZONTAL_PADDING) /
      X_AXIS_LABEL_CHARACTER_WIDTH,
  );

  return Math.max(
    MIN_X_AXIS_LABEL_LENGTH,
    Math.min(labelLength, MAX_X_AXIS_LABEL_LENGTH),
  );
}

function getXAxisLabelWidth(width?: number, visibleTicksCount?: number) {
  if (!width || !visibleTicksCount) {
    return (
      MAX_X_AXIS_LABEL_LENGTH * X_AXIS_LABEL_CHARACTER_WIDTH +
      X_AXIS_LABEL_HORIZONTAL_PADDING
    );
  }

  return width / visibleTicksCount;
}

function getXAxisLabelViewportX(
  textAnchor: `end` | `middle` | `start`,
  labelWidth: number,
) {
  if (textAnchor === `start`) {
    return 0;
  }

  return textAnchor === `end` ? -labelWidth : -labelWidth / 2;
}

function getXAxisLabelTextX(
  textAnchor: `end` | `middle` | `start`,
  labelWidth: number,
) {
  const horizontalInset = X_AXIS_LABEL_HORIZONTAL_PADDING / 2;

  if (textAnchor === `start`) {
    return horizontalInset;
  }

  return textAnchor === `end` ? labelWidth - horizontalInset : labelWidth / 2;
}

function getXAxisTextAnchor(index?: number, visibleTicksCount?: number) {
  if (index === 0) {
    return `start`;
  }

  if (index !== undefined && index === (visibleTicksCount ?? 0) - 1) {
    return `end`;
  }

  return `middle`;
}

export function parseRenderedTable(children: ReactNode): RenderedTable {
  const head = findRenderedComponent(children, MarkdownTableHead);
  const body = findRenderedComponent(children, MarkdownTableBody);
  const headerRow = findRenderedComponent(
    head?.props.children,
    MarkdownTableRow,
  );
  const headers = findRenderedComponents(
    headerRow?.props.children,
    MarkdownTableHeader,
  ).map(getRenderedChildren);
  const rows = findRenderedComponents(
    body?.props.children,
    MarkdownTableRow,
  ).map((row) =>
    findRenderedComponents(row.props.children, MarkdownTableCell).map(
      getRenderedChildren,
    ),
  );

  return { headers, rows };
}

function findRenderedComponent(
  children: ReactNode,
  component: React.ElementType,
) {
  return findRenderedComponents(children, component)[0];
}

function findRenderedComponents(
  children: ReactNode,
  component: React.ElementType,
) {
  return React.Children.toArray(children).filter(
    (child): child is ReactElement<{ children?: ReactNode }> =>
      isValidElement<{ children?: ReactNode }>(child) &&
      child.type === component,
  );
}

function getRenderedChildren(element: ReactElement<{ children?: ReactNode }>) {
  return element.props.children;
}
